// =============================================================
// main.js — entry point. Wires up loader, slider, lightbox,
// reveal animations, dot indicators, WhatsApp FAB.
// =============================================================

import { loadAllSections, getWhatsappFromData } from "./md-loader.js";
import { LooperSlider, bindInputs } from "./slider.js";
import { initLightbox } from "./lightbox.js";
import { initSlideshows, activateSlideshowsIn } from "./slideshow.js";
import { NavKnob } from "./knob.js";
import { playIntroIndex } from "./intro-index.js";

const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

const SECTION_IDS = [
  "section-hero",
  "section-experience",
  "section-perfect-for",
  "section-about",
  "section-gallery",
  "section-contact",
];

// Wait for CDN globals (marked + gsap) to load.
function waitForGlobals(timeout = 4000) {
  return new Promise((resolve) => {
    const start = Date.now();
    (function check() {
      if (window.marked && window.gsap) return resolve();
      if (Date.now() - start > timeout) return resolve(); // proceed anyway
      requestAnimationFrame(check);
    })();
  });
}

// Reveal in-section elements when slide becomes active.
function revealIn(slideEl) {
  if (!window.gsap) return;
  const targets = $$("[data-reveal], [data-split]", slideEl);
  if (!targets.length) return;

  window.gsap.set(targets, { opacity: 0, y: 24 });
  window.gsap.to(targets, {
    opacity: 1,
    y: 0,
    duration: 0.85,
    ease: "expo.out",
    stagger: 0.05,
    delay: 0.15,
  });

  const splits = $$("[data-split]", slideEl);
  if (window.SplitText && splits.length) {
    splits.forEach((el) => {
      try {
        const split = new window.SplitText(el, { type: "chars,words" });
        window.gsap.from(split.chars, {
          opacity: 0,
          y: 32,
          rotateX: -45,
          stagger: 0.022,
          duration: 0.65,
          ease: "back.out(1.6)",
          delay: 0.2,
        });
      } catch (_) { /* swallow split errors silently */ }
    });
  }
}

function updateLoopCounter(idx) {
  const numEl = $(".loop-counter .loop-num");
  if (numEl) numEl.textContent = String(idx + 1).padStart(2, "0");
}

function pulseKnob(isLoop = false) {
  if (isLoop) {
    const knob = $(".nav-knob");
    if (knob) {
      knob.classList.remove("is-loop-flash");
      void knob.offsetWidth;
      knob.classList.add("is-loop-flash");
      setTimeout(() => knob.classList.remove("is-loop-flash"), 800);
    }
  }
}

let navTimeout;
function wakeNav() {
  const nav = $(".nav-controls");
  if (!nav) return;
  nav.classList.add("is-visible");
  clearTimeout(navTimeout);
  navTimeout = setTimeout(() => {
    nav.classList.remove("is-visible");
  }, 2000);
}

// Pause/play hero video depending on active state. (mp4 only)
function manageHeroVideo(activeEl) {
  const activeVideo = activeEl.querySelector("video.hero-video");
  $$(".slide video.hero-video").forEach((v) => {
    if (v === activeVideo) v.play?.().catch(() => {});
    else v.pause?.();
  });
}

function logoEntrance() {
  if (!window.gsap) return;
  window.gsap.from(".logo-mark", {
    opacity: 0, scale: 0.4, rotation: -30,
    duration: 0.9, ease: "back.out(1.6)",
    clearProps: "opacity,scale,rotation,transform",
  });
  window.gsap.from(".loop-counter", {
    opacity: 0, x: 30,
    duration: 0.7, delay: 0.3, ease: "power2.out",
    clearProps: "opacity,transform",
  });
  // Don't animate opacity on .nav-controls — CSS owns it (fade on hover/active).
  window.gsap.from(".nav-controls", {
    y: 30,
    duration: 0.7, delay: 0.5, ease: "power2.out",
    clearProps: "transform",
  });
}

function pulseCounter(isLoop = false) {
  if (!window.gsap) return;
  const num = $(".loop-counter .loop-num");
  if (!num) return;
  window.gsap.fromTo(num,
    { scale: isLoop ? 1.6 : 1.25, color: isLoop ? "#ff5cf2" : "#ffffff" },
    { scale: 1, color: "#ffffff", duration: isLoop ? 0.7 : 0.4, ease: "elastic.out(1, 0.5)" }
  );
}

// Mouse-tracked spotlight: writes --mx / --my CSS vars on cards under cursor.
// Picked up by .audience-card-glow radial gradient for an interactive shine.
function bindAudienceSpotlight(root = document) {
  const cards = root.querySelectorAll(".audience-card");
  cards.forEach((card) => {
    card.addEventListener("pointermove", (e) => {
      const r = card.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * 100;
      const y = ((e.clientY - r.top) / r.height) * 100;
      card.style.setProperty("--mx", `${x}%`);
      card.style.setProperty("--my", `${y}%`);
    });
    card.addEventListener("pointerleave", () => {
      card.style.removeProperty("--mx");
      card.style.removeProperty("--my");
    });
  });
}

// Floating WhatsApp button — built from contact frontmatter.
function setupWhatsappFab(allData) {
  const fab = $("#whatsapp-fab");
  if (!fab) return;
  const wa = getWhatsappFromData(allData);
  if (!wa) {
    fab.hidden = true;
    return;
  }
  fab.href = wa.href;
  fab.querySelector(".fab-label").textContent = wa.label;
  fab.hidden = false;

  if (window.gsap) {
    window.gsap.from(fab, {
      opacity: 0,
      scale: 0.5,
      y: 20,
      duration: 0.8,
      delay: 1.2,
      ease: "back.out(1.8)",
    });
  }
}

// Boot
(async function init() {
  await waitForGlobals();

  if (window.marked) {
    window.marked.setOptions({ gfm: true, breaks: true });
  }

  const slidesRoot = $(".slides-track");
  const { data: allData } = await loadAllSections(slidesRoot);

  const slides = $$(".slide", slidesRoot);

  const slider = new LooperSlider($("#slider"), slides, {
    duration: 0.95,
    onChange: (idx, info) => {
      wakeNav();
      updateLoopCounter(idx);
      pulseKnob(info.isLoop);
      pulseCounter(info.isLoop);
      const active = slides[idx];
      manageHeroVideo(active);
      activateSlideshowsIn(active);
    },
  });

  bindInputs(slider);
  const navKnobEl = $(".nav-knob");
  if (navKnobEl) new NavKnob(navKnobEl, slider);
  initLightbox(document);
  initSlideshows(document);
  bindAudienceSpotlight(document);
  setupWhatsappFab(allData);

  updateLoopCounter(0);
  manageHeroVideo(slides[0]);
  activateSlideshowsIn(slides[0]);
  logoEntrance();

  // Intro index splash (3s, sweep L→R) — hides .nav-controls during play.
  // wakeNav() runs after intro finishes so the nav greets the user.
  playIntroIndex({
    orientation: "horizontal",
    duration: 3000,
    hideEl: ".nav-controls",
  }).then(() => wakeNav());

  // Hash-based deep link
  const hashIdx = SECTION_IDS.indexOf(location.hash.replace("#", ""));
  if (hashIdx > 0) slider.goTo(hashIdx);
})();
